import { Fragment, useEffect } from 'react';
import ContentLoader from 'react-content-loader';
import { useNavigate, useParams } from 'react-router-dom';
import Container from '../../../../core/components/atoms/Container';
import Lesson from '../../../../core/components/organism/Lesson';
import NoContent from '../../../../core/components/templates/NoContent/index';
import { markLessonFinish } from '../../../../core/queries/lessons';
import { LessonBreadcrumbsHeader } from '../../components/LessonHeader/inedx';
import ContentFooter from '../../components/contentFooter';
import { useContentLesson } from '../../hooks/useContentLesson';
import { useLesson } from '../../hooks/useLesson';
import { useLessonContentStore } from '../../stores/lessonContentStore';
import { useNewModuleStore } from '../../stores/newModuleStore';
import { useAccessControlStore } from '../../../../core/stores/accessControlStore';
import Lock from '../../../../core/components/organism/Lock';
import { validateLoadContent } from '../../../../core/utils/freeValidations';

export default function ContentLesson() {
  const { discipline, module, subject, slug } = useParams();
  const navigate = useNavigate();

  const { isFreeUser } = useAccessControlStore();
  const { isLoading, isError } = useContentLesson(slug || '', isFreeUser);
  const { moduleSlug } = useNewModuleStore();
  const { mutate } = useLesson();

  const {
    lesson,
    currentLessonIndex,
    allLessons,
    nextLink,
    previousLink,
    setNextAndPreviousLesson,
  } = useLessonContentStore();

  useEffect(() => {
    if (!validateLoadContent(slug || '')) {
      return;
    }

    if (
      module &&
      (allLessons.length === 0 || !moduleSlug || moduleSlug !== module)
    ) {
      mutate(module);
    }
  }, [module]);

  if (!validateLoadContent(slug || '')) {
    return <Lock />;
  }

  useEffect(() => {
    const baseLink = `/disciplinas/${discipline}/modulo/${module}/assunto/${subject}/videoaula`;
    setNextAndPreviousLesson(baseLink);
  }, [currentLessonIndex, allLessons]);

  if (isLoading) {
    return (
      <Container direction='col' gap='xxl' className='lg:py-18 py-24'>
        <ContentLoader />
      </Container>
    );
  }

  if (isError) {
    const url = `/disciplinas/${discipline}/modulo/${module}/tipo/videoaula`;

    return <NoContent url={url} />;
  }

  if (!lesson) {
    return <></>;
  }

  const nextLesson = () => {
    if (nextLink) {
      navigate(nextLink);
    }
  };

  const previousLesson = () => {
    if (previousLink) {
      navigate(previousLink);
    }
  };

  const {
    slug: contentSlug,
    title,
    ancestors,
    teacher,
    timeline,
    duration,
    sambatech_token,
    vimeoID,
    defaultPlayer,
    embedUrl,
    pdfSummaryUri,
    favorite,
    review,
    _id,
    subjects,
  } = lesson;

  function dispatchSetContentAsDoneEvent() {
    const setContentAsDone = new CustomEvent('setContentAsDone', {
      detail: {
        contentId: _id,
      },
    });
    window.dispatchEvent(setContentAsDone);
  }

  const onFinish = () => {
    if (slug) {
      markLessonFinish(slug, null);
      dispatchSetContentAsDoneEvent();
    }
  };

  if (ancestors.level.slug !== 'superior') {
    return <Lock />;
  }

  // Quando o default é o player novo (aprova), o player antigo ainda precisa
  // funcionar via toggle. Como 'aprova' não é um tipo válido para o <Player>
  // clássico, derivamos um player legado a partir dos tokens disponíveis.
  const legacyPlayerType =
    defaultPlayer === 'aprova'
      ? vimeoID
        ? 'vimeo'
        : 'sambatech'
      : defaultPlayer;
  const legacyToken = legacyPlayerType === 'vimeo' ? vimeoID : sambatech_token;

  return (
    <Fragment>
      <Container direction='col' gap='xxl' className='lg:pb-10'>
        <LessonBreadcrumbsHeader />
        <Lesson
          id={_id}
          module={module}
          isFavorite={favorite}
          review={review}
          title={title}
          discipline={ancestors.area.slug}
          time={duration}
          teacher={(teacher || [])
            .map((it: { name: string }) => it.name)
            .join(', ')}
          timeline={timeline}
          playerType={legacyPlayerType}
          token={legacyToken}
          embedUrl={embedUrl}
          defaultPlayer={defaultPlayer}
          pdfSummary={pdfSummaryUri}
          onFinish={onFinish}
          contentSlug={contentSlug}
          subjects={subjects}
          subjectRelated={subjects[0].slug}
          isDisciplines
          showQuiz
        />
      </Container>

      <ContentFooter
        previousLabel='Videoaula anterior'
        nextLabel='Próxima videoaula'
        nextContent={nextLesson}
        previousContent={previousLesson}
        currentIndex={currentLessonIndex}
        contentModalInitialTab={0}
        contentModalSubtitle='Lista de Videoaulas'
        contentLength={allLessons.length || 0}
        mainLabel='Playlist de videoaulas'
        contentType='videoaula'
      />
    </Fragment>
  );
}
